<template>
  <article class="itp-used-list card-image" @click.prevent="emit('select', callInfo)">
    <div class="left-col">
      <div class="logo-box">
        <img :src="imageUrlChatroom" alt="" />
      </div>
      <div class="room-info">
        <div class="inr">
          <div>
            <InterpretingBadge v-if="callTypeInfo.label" :label="callTypeInfo.label" :type="callTypeInfo.type" />
            <!--            <p class="personnal">{{ t('label_capacity') }} {{ formatNumberWithComma(statusInfo.participantCount) }}</p>-->
          </div>
          <IconFavorite
            class="icon"
            :is-favorite="callInfo.isFavorite"
            @click.stop="emit('toggle-favorite', callInfo)"
          />
        </div>
        <div class="meta-title">
          <p class="accent tit">{{ callInfo.title }}</p>
        </div>
      </div>
    </div>

    <div class="right-col">
      <button ref="ref-button-edit" type="button" class="txt-btn" @click.stop="openDropdownEdit">
        {{ t('label_edit') }}
      </button>
      <p class="date">
        {{ formatDateTimeByLocale(callInfo.eventTime) }}
      </p>
    </div>

    <Teleport to="body">
      <ModalShareChat v-if="activeModalName === 'share'" :call-info="callInfo" @close="handelActiveModalName()" />
      <DownloadTableModal
        v-else-if="activeModalName === 'download'"
        :code="callInfo.code"
        :chat-type="callInfo.callType"
        @close="handelActiveModalName()"
      />
      <ModalSmall
        v-else-if="activeModalName === 'edit'"
        class="modify-modal"
        :title="t('label_edit_interpretation')"
        @close="handelActiveModalName()"
      >
        <SelfServiceChatEdit
          domain="SELF_SERVICE"
          :data="callInfo"
          :has-delete="false"
          @updated="refreshAfterRequest"
        />
      </ModalSmall>
      <DropdownEdit
        v-if="isOpenDropdownEdit"
        :is-visible="isOpenDropdownEdit"
        :target-el="refButtonEdit"
        @close="closeDropdownEdit"
      >
        <!--수정하기-->
        <button class="md-pop-btn m-btn" @click.stop="handelActiveModalName('edit')">
          <IconModify class="icon" />
          <span>{{ t('button_edit') }}</span>
        </button>
        <!-- 공유하기 -->
        <button class="md-pop-btn m-btn" @click.stop="handelActiveModalName('share')">
          <img src="/public/icons/qr-icon.svg" alt="qr" />
          <span>{{ t('invite_qr_code') }}</span>
        </button>
        <!--다운로드-->
        <button
          v-if="isHistoryDownloadEnabled"
          class="md-pop-btn m-btn"
          @click.stop="handelActiveModalName('download')"
        >
          <IconDownload class="icon" />
          <span>{{ t('download') }}</span>
        </button>
        <!--삭제하기-->
        <button class="md-pop-btn m-btn" @click.stop="deleteChatModal">
          <IconTrash class="icon" />
          <span>{{ t('button_delete') }}</span>
        </button>
      </DropdownEdit>
    </Teleport>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { deleteCall } from '@/api/apiService';
import DownloadTableModal from '@/components/domains/chat/script-download/ChatScriptDownloadModal.vue';
import IconDownload from '@/components/jh-app/icons/IconDownload.vue';
import IconFavorite from '@/components/jh-app/icons/IconFavorite.vue';
import IconModify from '@/components/jh-app/icons/IconModify.vue';
import IconTrash from '@/components/jh-app/icons/IconTrash.vue';
import SelfServiceChatEdit from '@/components/self-service/chat/SelfServiceChatEdit.vue';
import DropdownEdit from '@/components/shared/base/DropdownEdit.vue';
import ModalShareChat from '@/components/shared/composite/ModalShareChat.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { useAlertStore } from '@/stores/alert';
import { useToastStore } from '@/stores/toast';
import { UserType, useUserStore } from '@/stores/user';
import { IResponseContractCustomerPricing, IResponseMultiCallsDashboard } from '@/types/response';
import formatDateTimeByLocale from '@/utils/common/formatDateTimeByLocale';

import InterpretingBadge from '../base/InterpretingBadge.vue';

const refButtonEdit = useTemplateRef('ref-button-edit');
const isOpenDropdownEdit = ref(false);

const { t, locale } = useI18n();

const alertStore = useAlertStore();
const toastStore = useToastStore();
const userStore = useUserStore();

const props = withDefaults(
  defineProps<{
    callInfo: IResponseMultiCallsDashboard;
    unitPriceList: IResponseContractCustomerPricing[];
    usingActions?: boolean;
  }>(),
  {
    callInfo: () => ({}) as IResponseMultiCallsDashboard,
    unitPriceList: () => ({}) as IResponseContractCustomerPricing,
    usingActions: false,
  }
);

const emit = defineEmits<{
  open: [];
  refresh: []; // 통화 목록 갱신
  select: [callInfo: IResponseMultiCallsDashboard];
  'toggle-favorite': [id: IResponseMultiCallsDashboard];
}>();

// 통화방 공유 모달 노출 여부
const activeModalName = ref<string>('');

const isHistoryDownloadEnabled = computed<boolean>(() => {
  const { storeAgreement, userType } = userStore.userInfo;
  return storeAgreement && UserType.isPartner(userType);
});

// 통화 상태 태그 반환 (종료/진행중)
const statusInfo = computed<Record<string, string>>(() => {
  return (
    {
      CALLING: {
        label: t('dashboard_call_status_calling'),
        labelEventTime: t('dashboard_call_time_start'),
        labelParticipants: t('dashboard_participants_current'),
        participantCount: props.callInfo.attendeeCount,
        type: 'success-light',
      },
      DONE: {
        label: t('dashboard_call_status_done'),
        labelEventTime: t('dashboard_call_time_end'),
        labelParticipants: t('label_max_participants'),
        participantCount: props.callInfo.limitCount,
        type: 'gray-light',
      },
    }[props.callInfo.callStatus] || {}
  );
});

// 통역 종류 태그 타입 매핑
const callTypeInfo = computed<Record<string, string>>(() => {
  return (
    {
      GENERAL: {
        label: t('label_general'),
        type: 'info-light general',
      },
      CONFERENCE: {
        label: t('label_conference'),
        type: 'info-light conference',
      },
      MEETING: {
        label: t('label_meeting'),
        type: 'info-light meeting',
      },
      SINGLE: {
        label: t('label_single'),
        type: 'success-light',
      },
      AUDIENCE: {
        label: t('label_audience'),
        type: 'gray-light',
      },
    }[props.callInfo.callType] || { label: '', type: 'gray-light' }
  );
});

// 마지막 통역 시간/ 통역 시작 시간 반환
const textCallTimestamp = computed<string>(() => {
  const textLocale = locale.value === 'en_US' ? 'en-US' : locale.value;
  return new Date(Number(props.callInfo.eventTime)).toLocaleString(textLocale, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
});

// 통역방 이미지 URL
const imageUrlChatroom = computed<string>(() => {
  return props.callInfo.callLogoImageUrl || '/images/logo-sconai.png';
});

const handelActiveModalName = (name?: string) => {
  activeModalName.value = name ? name : '';
};

const deleteChatModal = () => {
  const { code = '' } = props.callInfo;

  alertStore.confirm(
    {
      title: t('button_delete'),
      message: t('alert_confirm_delete'),
      subMessage: t('alert_delete_irreversible'),
    },
    {
      confirm: async () => {
        const response = await deleteCall(code);
        if (response.success) {
          emit('refresh');
          toastStore.info(t('alert_deleted'));
        }
      },
    }
  );
};

const refreshAfterRequest = () => {
  activeModalName.value = '';
  emit('refresh');
};

const openDropdownEdit = () => {
  isOpenDropdownEdit.value = true;
};

const closeDropdownEdit = () => {
  isOpenDropdownEdit.value = false;
};
</script>

<style scoped lang="scss">
article {
  z-index: $z-card;
}

.itp-used-list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0.5rem 1rem 1rem;
  border-radius: $size-m;
  transition:
    transform 0.2s,
    background-color 0.1s;
  cursor: pointer;
  &:hover {
    background-color: $color-gray-10;
  }
  &:active {
    transform: scale(0.98);
  }
  .left-col {
    display: flex;
    align-items: center;
    gap: $size-s;
    .logo-box {
      width: 48px;
      height: 48px;
      border-radius: 1rem;
      border: 1px solid $color-gray-40;
      overflow: hidden;
      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
    .room-info {
      .inr {
        display: flex;
        align-items: center;
        gap: $size-s;
        margin-bottom: 2px;
        div {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: $size-xs;
          .personnal {
            color: $color-gray-200;
            font-size: $font-size-xs;
          }
        }
      }
      .tit {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        max-width: 140px;
      }
    }
  }
  .right-col {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    .txt-btn {
      font-size: $font-size-psmall;
      padding: 0.5rem;
      max-height: 32px;
      border-radius: $size-s;
      &:hover {
        background: $gray10;
      }
    }
    .date {
      color: $color-gray-200;
      font-size: $font-size-psmall;
      text-align: right;
      padding-right: 0.5rem;
    }
  }
}

.meta-title {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: 0.5rem;

  h4 {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.icon-dim.favorited {
  fill: var(--primary) !important;
}
.md-pop-btn {
  justify-content: flex-start;
  padding: 0.5rem;
  &:hover {
    background: $gray10;
  }
}

.modify-modal {
  :deep(main) {
    padding: 0;
    display: block;
  }
}
</style>
